import { useState, useEffect, useCallback, useRef } from 'react';
import { rmmAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck, ShieldAlert, Upload, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

// Admin: shows which certificate signs the RMM .exe files and lets a publicly trusted
// (OV/EV) code-signing certificate be uploaded so Windows SmartScreen / AV trust the installer.
export const SigningCertCard = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(() => rmmAPI.signingCertInfo().then((r) => setInfo(r.data)).catch(() => setInfo({ present: false })), []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Choose a .pfx / .p12 certificate file first');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('pfx', file);
      fd.append('password', password);
      const r = await rmmAPI.uploadSigningCert(fd);
      toast.success(r.data.message);
      setPassword('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); } finally { setBusy(false); }
  };

  if (!info) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>;
  const trusted = info.present && !info.self_signed;

  return (
    <Card className="bg-white border-slate-200" data-testid="rmm-signing-card">
      <CardHeader>
        <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
          {trusted ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
          Trusted publisher &amp; antivirus reputation
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-slate-600 space-y-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1" data-testid="rmm-signing-info">
          <p><b>Installer currently signed by:</b> <span data-testid="rmm-signing-subject">{info.subject || '—'}</span></p>
          <p><b>Issued by:</b> {info.issuer || '—'} {info.not_after && <span className="text-slate-400">· valid until {new Date(info.not_after).toLocaleDateString()}</span>}</p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {trusted
              ? <Badge className="bg-emerald-100 text-emerald-700">Publicly trusted certificate</Badge>
              : <Badge className="bg-amber-100 text-amber-700">Self-signed (MVP IT) — SmartScreen will warn on first run</Badge>}
            {info.custom && <Badge variant="outline">Custom certificate</Badge>}
            {!info.osslsigncode && <Badge className="bg-red-100 text-red-700">Signing tool missing on server</Badge>}
          </div>
        </div>

        <p>
          What the installer already does on every PC: it is Authenticode-signed (SHA-256 + timestamp), carries publisher/version metadata,
          requests admin rights through the standard UAC prompt, and <b>automatically trusts the MVP IT certificate</b> (Trusted Root + Trusted Publishers)
          so the agent, uninstaller and remote-control helper never trigger publisher warnings afterwards.
        </p>
        <p>
          To remove the very first "Windows protected your PC" / antivirus reputation warning on brand-new machines, Windows requires a certificate
          from a public Certificate Authority. Buy an <b>OV or EV code-signing certificate</b> (DigiCert, Sectigo, SSL.com, GlobalSign — roughly $200–400/yr),
          export it as <b>.pfx</b> and upload it here. All RMM binaries are re-signed instantly and new downloads use it.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-slate-700">Certificate file (.pfx / .p12)</Label>
            <Input ref={fileRef} type="file" accept=".pfx,.p12" className="bg-white border-slate-200 mt-1" data-testid="rmm-signing-file-input" />
          </div>
          <div>
            <Label className="text-slate-700">Certificate password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white border-slate-200 mt-1" data-testid="rmm-signing-password-input" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={upload} disabled={busy || !info.osslsigncode} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="rmm-signing-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload &amp; re-sign installers
          </Button>
          <Button variant="outline" className="rounded-full" disabled={busy || !info.osslsigncode} onClick={() => act(rmmAPI.resignBinaries, 'Binaries re-signed')} data-testid="rmm-signing-resign-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Re-sign now
          </Button>
          {info.custom && (
            <Button variant="ghost" className="rounded-full text-slate-500" disabled={busy} onClick={() => act(rmmAPI.removeSigningCert, 'Reverted to the MVP IT certificate')} data-testid="rmm-signing-revert-btn">
              <Undo2 className="w-4 h-4 mr-1" /> Revert to MVP IT certificate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
